import { bindActionCreators } from 'redux';
import { resource as rm, debounce } from 'utils';
import { headerConnect as connect } from 'components/Shared/connect';
import {
  searchAutocompleteFetchData,
  searchClose,
  searchOpen,
  SEARCH_MINIMUM_LENGTH,
  searchAutocompleteDataLoaded
} from 'components/Search/actions';
import styles from './styles.scss';
import Overlay from 'components/Shared/Overlay';
import cn from 'classnames';
import { AutoCompleteSuggestion } from 'models/AutoCompleteSuggestion';
import AutoComplete from './autoComplete';
import { getPage } from 'components/Pagelayout/actions';
import { createHref, IHref } from 'components/Shared/server';
import { ILocation } from 'models/ILocation';
import { FormEvent, Component, createRef } from 'react';
import whitelist from 'components/Shared/whitelist';
import { AppShellManifest } from 'models/AppShellManifest';
import { hasSearchResult } from 'components/Search/selector';
import { keyDown } from './actions';

const SEARCH_TIMEOUT = 300;

interface OwnProps {
  className?: string;
  buttonComponent?: JSX.Element;
}

interface MergedProps extends OwnProps {
  searchButton: string;
  searchPlaceHolder: string;
  searchAutocompleteFetchData: (searchString: string) => void;
  searchAutocompleteDataloaded: (suggestion?: AutoCompleteSuggestion) => void;
  searchClose: () => void;
  searchOpen: () => void;
  keyDown: (key: string) => void;
  getPage: (href: IHref) => void;
  searchSuggestions: AutoCompleteSuggestion;
  searchUrl: string;
  showResult: boolean;
  txtNoHits: string;
  txtDidYouMean: string;
  txtBrand: string;
  txtCategories: string;
  txtProducts: string;
  location: ILocation;
  whitelist: AppShellManifest['whitelist'];
}

class Search extends Component<MergedProps> {
  public state = { searchString: '' };

  private searchRef = createRef<HTMLInputElement>();
  private suggestionRef = createRef<HTMLInputElement>();
  private fetchAutocompleteData = debounce((str: string) => {
    this.props.searchAutocompleteFetchData(str);
  }, SEARCH_TIMEOUT);

  public onChange = () => {
    const searchInput = this.searchRef.current;
    if (!searchInput) {
      return;
    }
    const searchString = searchInput.value;
    this.setState({ searchString });

    if (searchString.length >= SEARCH_MINIMUM_LENGTH) {
      this.fetchAutocompleteData(searchString);
    } else {
      // Resets the search result, hiding the drop down
      this.props.searchAutocompleteDataloaded(undefined);
    }
  };

  public onKeyUp = () => {
    const searchInput = this.searchRef.current;
    if (!searchInput) {
      return;
    }
    searchInput.value = searchInput.value.toLowerCase();
  };

  public onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Tab' || e.key === 'Escape') {
      this.props.searchClose();
      return;
    }

    if (
      e.key === 'Enter' &&
      this.props.searchSuggestions !== undefined &&
      this.props.searchSuggestions.selectedItemIndex > -1
    ) {
      e.preventDefault();
      let selectedItem;
      if (this.props.searchSuggestions.selectedColumnIndex === 0) {
        selectedItem = this.props.searchSuggestions.suggestedProducts.filter(
          product => product.selected
        )[0];
      } else {
        if (
          this.props.searchSuggestions.suggestedBrandCategories.length > 0 &&
          this.props.searchSuggestions.selectedItemIndex < 3
        ) {
          selectedItem = this.props.searchSuggestions.suggestedBrandCategories.filter(
            category => category.selected
          )[0];
        } else {
          selectedItem = this.props.searchSuggestions.suggestedCategories.filter(
            category => category.selected
          )[0];
        }
      }
      if (selectedItem !== undefined) {
        window.location.href = selectedItem.url;
      }
    } else if (
      e.key === 'ArrowRight' &&
      this.props.searchSuggestions !== undefined &&
      this.props.searchSuggestions.selectedItemIndex === -1 &&
      this.props.searchSuggestions.suggestedPhrases.length > 0
    ) {
      const searchInput = this.searchRef.current;
      if (!searchInput) {
        return;
      }
      searchInput.value = this.props.searchSuggestions.suggestedPhrases[0].phrase;

      this.onChange();
    }

    this.setSuggestionStrings();
    this.props.keyDown(e.key);
  };

  public componentDidUpdate() {
    this.setSuggestionStrings();
  }

  public setSuggestionStrings() {
    const searchInputSuggestion = this.suggestionRef.current;
    if (!searchInputSuggestion) {
      return;
    }
    if (
      this.props.searchSuggestions !== undefined &&
      this.props.searchSuggestions.suggestedPhrases.length > 0
    ) {
      searchInputSuggestion.value = this.props.searchSuggestions.suggestedPhrases[0].phrase;
    } else {
      searchInputSuggestion.value = '';
    }
  }

  public render() {
    const {
      searchButton,
      searchPlaceHolder,
      searchSuggestions,
      searchClose,
      searchOpen,
      searchUrl,
      showResult,
      className,
      buttonComponent,
      txtNoHits,
      txtDidYouMean,
      txtBrand,
      txtCategories,
      txtProducts
    } = this.props;

    return (
      <div className={cn(styles.wrapper, className)}>
        <Overlay
          wrapperClassname={styles.overlayWrapper}
          visible={showResult}
          onClick={searchClose}
        >
          <form
            className={styles.searchContainer}
            action={searchUrl}
            onSubmit={this.executeSearch}
            method="get"
          >
            <span className={styles.inputbackground}>
              <input
                ref={this.searchRef}
                className={styles.realInput}
                autoComplete="off"
                type="search"
                name="phrase"
                placeholder={searchPlaceHolder}
                onChange={this.onChange}
                onFocus={searchOpen}
                onKeyDown={this.onKeyDown}
                onKeyUp={this.onKeyUp}
                id="search"
                enterKeyHint={'search'}
              />
              <input
                ref={this.suggestionRef}
                type="search"
                id="searchInputSuggestion"
                className={styles.suggestionInput}
                disabled
              />
            </span>
            {buttonComponent ? (
              buttonComponent
            ) : (
              <button className={styles.button}>{searchButton}</button>
            )}
            {showResult && hasSearchResult(searchSuggestions) && (
              <AutoComplete
                searchSuggestions={searchSuggestions}
                onSelect={searchClose}
                searchUrl={searchUrl}
                txtNoHits={txtNoHits}
                txtDidYouMean={txtDidYouMean}
                txtBrand={txtBrand}
                txtCategories={txtCategories}
                txtProducts={txtProducts}
              />
            )}
          </form>
        </Overlay>
      </div>
    );
  }

  private useReactSearch() {
    const isReactSearchEnabled = whitelist(this.props.whitelist, createHref(this.props.searchUrl));
    return isReactSearchEnabled;
  }

  private executeSearch = (e: FormEvent) => {
    const { searchClose, location, searchUrl, getPage } = this.props;
    const searchRef = this.searchRef.current;
    const suggestionRef = this.suggestionRef.current;

    searchClose();

    if (!this.useReactSearch() || !searchRef || !window.isReact || !suggestionRef) {
      return;
    }

    e.preventDefault();
    const phrase = searchRef.value;
    searchRef.value = '';
    suggestionRef.value = '';

    return getPage(createHref({ ...location, pathname: searchUrl, search: { phrase } }));
  };
}

export default connect<MergedProps, OwnProps>(
  state => ({
    searchButton: rm(state.appShell, 'header_search_button'),
    searchPlaceHolder: rm(state.appShell, 'header_search_placeholder'),
    txtNoHits: rm(state.appShell, 'search_new_nohits'),
    txtDidYouMean: rm(state.appShell, 'search_new_didyoumean'),
    txtBrand: rm(state.appShell, 'search_new_brand'),
    txtCategories: rm(state.appShell, 'search_new_categories'),
    txtProducts: rm(state.appShell, 'search_new_products'),
    searchSuggestions: state.search.suggestion,
    searchUrl: state.search.searchUrl,
    showResult: state.search.showResult,
    location: state.location,
    whitelist: state.appShell.whitelist
  }),
  dispatch =>
    bindActionCreators(
      {
        searchAutocompleteFetchData,
        searchClose,
        searchOpen,
        keyDown,
        getPage,
        searchAutocompleteDataloaded: searchAutocompleteDataLoaded
      },
      dispatch
    )
)(Search);
